import { ApolloClient, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import axios from 'axios';
import { FetcherOpts, FetcherParams } from '@graphiql/toolkit';
import { useStoreState } from 'hooks';
import { useCallback } from 'react';
import { getProjectGraphQLUrl } from 'utils/graphql-url';
import { useLogin } from 'store/auth';
import { useQuery } from './useQuery';

const useBearerToken = () => useStoreState((state) => state.auth.token);
const useTenant = () => useStoreState((state) => state.auth.tenant);
const useCluster = () => useStoreState((state) => state.auth.cluster);

export const useApolloAuthLink = () => {
  const token = useBearerToken();
  const project = useTenant();
  const cluster = useCluster();
  const expiry = useStoreState((state) => state.auth.expiry);
  const method = useQuery.Method();
  const login = useLogin();
  return setContext(async (_, { headers }) => {
    // Expiry from token is in seconds
    if (expiry && expiry < Date.now() / 1000) {
      await login({ tenant: project, useOIDC: method === 'oauth' });
    }
    // get the authentication token from local storage if it exists
    // return the headers to the context so httpLink can read them
    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${token}`,
        project,
        ...(cluster && { cluster }),
      },
    };
  });
};

export const useGraphQLFetcher = (url: string) => {
  const bearerToken = useBearerToken();
  const project = useTenant();
  const cluster = useCluster();

  return useCallback(
    async (graphQLParams: Partial<FetcherParams>, opts?: FetcherOpts) => {
      try {
        const response = await axios.post(url, graphQLParams, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${bearerToken}`,
            project,
            cluster,
            ...opts?.headers,
          },
        });
        return response.data;
      } catch (e) {
        const err: any = e as any;
        if (!err?.response?.data?.errors) {
          return err?.response?.data;
        }
        const error = err.response.data.errors.map((res: any) => {
          if (res.extensions && res.extensions.code) {
            res.code = res.extensions.code;
            delete res.extensions;
          }
          return res;
        });
        return { error };
      }
    },
    [bearerToken, project, cluster, url]
  );
};

const useFDMApolloAuthLink = () => {
  const token = useBearerToken();
  const project = useTenant();
  const expiry = useStoreState((state) => state.auth.expiry);
  const method = useQuery.Method();
  const login = useLogin();

  return setContext(async (_, { headers }) => {
    // Expiry from token is in seconds
    if (expiry && expiry < Date.now() / 1000) {
      await login({ tenant: project, useOIDC: method === 'oauth' });
    }
    // get the authentication token from local storage if it exists
    // return the headers to the context so httpLink can read them
    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${token}`,
      },
    };
  });
};

export const useFDMApolloClient = (
  schemaCollectionRefId: string,
  headers: any
) => {
  const apolloAuthLink = useFDMApolloAuthLink();

  return new ApolloClient({
    link: apolloAuthLink.concat(
      createHttpLink({
        uri: getProjectGraphQLUrl(schemaCollectionRefId),
        headers,
      })
    ),
    cache: new InMemoryCache({
      // Apollo automatically adds __typename to queries. This was causing
      // a bug when using orderBy. See https://github.com/neo4j-graphql/neo4j-graphql-js/issues/581
      addTypename: false,
    }),
  });
};
